import { Button } from "@progress/kendo-react-buttons";
import React, { Suspense, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import useLocale from "../../hooks/useLocale";
import insightsService from "../../services/insights.service";
import { Dictionary } from "../../types/Dictionary";
import {
  Insight,
  InsightCallDetails,
  InsightCallFilterMetrics,
  InsightMetricKeyLabel,
  InsightsFilter,
} from "../../types/insight";
import { InsightFilterMetric } from "./Insights";
import { CallDetail } from "../../types/call";
import { ColorCategory } from "../../types/master-data";
import CallListing from "../../components/custom/CallListing";
import { searchCallData } from "../../utils/callDetailUtil";
import CustomSearchFieldTextInput from "../../components/custom/form/CustomSearchFieldTextInput";
import { useLocation } from "react-router-dom";
import useLocalStorage from "../../hooks/useLocalStorage";
import { LocalStorageFilter } from "../../types/localstorage-filters/LocalStorageFilter";
import useSwal from "../../hooks/useSwal";
import { Page } from "../../types/Page";
import { CallTableSortColumnEnum } from "../../enums/CallTableSortColumnEnum";
import useTranslation from "../../hooks/useTranslation";
import ExportCallsDialog from "../../components/ExportCallsDialog";
import recordingsService from "../../services/recordings.service";
import { AxiosError } from "axios";
import { SweetAlertOptions } from "sweetalert2";
import LoadingOverlay from "../../components/LoadingOverlay";
import { AccessPermissionEnum } from "../../enums";
import { orderBy } from "@progress/kendo-data-query";
//const CallListing = React.lazy(()=>import("../../components/custom/CallListing"))
interface InsightCallsGridProps {
  docked?: boolean;
  insightsFilter?: InsightsFilter;
  insight?: Insight;
  viewByMetric: InsightMetricKeyLabel;
  filterMetric?: InsightFilterMetric;
  viewByMetricFilterValue?: {
    metricKeyLabel: string;
    metricKey: string;
    metricSubKeyLabel?: string;
    displayName?: string;
  };
  onUserBackClick: () => void;
}

const initialPageDetail: Page = {
  skip: 0,
  take: 10,
  totalRecords: 0,
  sort: {
    sortField: CallTableSortColumnEnum.DateTime,
    sortDirection: "desc"
  }
}

const InsightCallsGrid: React.FC<InsightCallsGridProps> = ({
  docked,
  insightsFilter,
  insight,
  viewByMetric,
  filterMetric,
  viewByMetricFilterValue,
  onUserBackClick,
}) => {
  const trans = useTranslation("InsightCallsGrid");
  const localeCtx = useLocale();
  const auth = useAuth();
  const swal = useSwal();
  const [calls, setCalls] = useState<InsightCallDetails | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [isBulkDownloading, setIsBulkDownloading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");

  // For checkbox
  const [checkedRows, setCheckedRows] = useState<number[]>([]);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false);
  const [isShowCheckedRows, setIsShowCheckedRows] = useState<boolean>(false);
  const [filteredCalls, setFilteredCalls] = useState<InsightCallDetails | undefined>(undefined);

  const [bulkDownloadSizeLimitMB, setBulkDownloadSizeLimitMB] = useState<number>(2048);
  const [bulkDownloadRowLimit, setBulkDownloadRowLimit] = useState<number>(10);

  useEffect(() => {
      // fetch bulk download limits once on mount
      let mounted = true;
      const fetchBulkLimits = async () => {
        try {
          const limits = await recordingsService.getBulkDownloadLimits();
          if (mounted && limits) {
            if (typeof limits.bulkDownloadSizeLimitInMbs === "number") {
              setBulkDownloadSizeLimitMB(limits.bulkDownloadSizeLimitInMbs);
            }
            if (typeof limits.bulkDownloadRowCountLimit === "number") {
              setBulkDownloadRowLimit(limits.bulkDownloadRowCountLimit);
            }
          }
        } catch (err) {
          console.warn("Failed to fetch bulk download limits", err);
        }
      };
      fetchBulkLimits();
      return () => {
        mounted = false;
      };
    }, []);

  useEffect(() => {
    fetchCalls(initialPageDetail);
  }, [insight, filterMetric, viewByMetric, viewByMetricFilterValue]);

  const fetchCalls = async (page: Page) => {
    try {
      // setCheckedRows([]);
      setCalls({ id: insight?.id ?? "", page: page, calls: [] });
      setLoading(true);
      setError(false);
      if (insight && insight.id) {
        const callFilterMetrics: InsightCallFilterMetrics = {
          insightId: insight.id,
          filterKey: filterMetric ? filterMetric.metricKeyLabel : undefined,
          filterSubKey: filterMetric
            ? filterMetric.metricKeySubLabel
            : undefined,
          filterValue: filterMetric ? filterMetric.metricKey : undefined,
          viewBy: viewByMetricFilterValue
            ? viewByMetricFilterValue.metricKeyLabel
            : undefined,
          viewByFilterSubKey: viewByMetricFilterValue
            ? viewByMetricFilterValue.metricSubKeyLabel
            : undefined,
          viewByFilterValue: viewByMetricFilterValue
            ? viewByMetricFilterValue.metricKey
            : undefined,
        };
        const resp = await insightsService.getInsightCallDetails(callFilterMetrics, page, search);
        const _callDetails: CallDetail[] = [];
        resp?.calls?.forEach((c) => {
          _callDetails.push({ ...c, expanded: false });
        });
        setCalls({ id: insight.id, page: resp.page, calls: _callDetails });
      }
    } catch (err) {
      setCalls({ id: insight?.id ?? "", page: page, calls: [] });
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCallFlagUpdate = async (
    selectedFlag: ColorCategory | null,
    callDetailData: CallDetail
  ) => {
    if (selectedFlag === null) {
      selectedFlag = { id: 0, name: "", hexCode: "" };
    }
    if (calls) {
      var tempCalls = calls.calls.map((cd) => {
        if (cd.platformCallDetailId === callDetailData.platformCallDetailId) {
          cd.colorCategory = selectedFlag !== null ? selectedFlag : undefined;
        }
        return cd;
      });
      setCalls({ ...calls, calls: tempCalls });
    }
  };

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value);
  };

  const handlePaginationChange = (pageDetail: Page) => {
    fetchCalls(pageDetail);
  }

  const handleSortChange = (sortField: string, sortDirection: string) => {
    let _pageFilter: Page = { ...initialPageDetail };
    _pageFilter.sort = {
      sortDirection: sortDirection,
      sortField: sortField
    }
    fetchCalls(_pageFilter);
  }

  const handleSearchTextOnKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      fetchCalls(initialPageDetail);
    }
  }

  const onExportBtnClick = () => {
    setIsExportDialogOpen(true);
  }

  const onCloseExportDialog = () => {
    setIsExportDialogOpen(false);
    handleClearAllRows();
  };

  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleExportDropdown = () => {
    setShowExportDropdown((prev) => !prev);
  };

  const handleExcelExport = () => {
    setShowExportDropdown(false);
    setIsExportDialogOpen(true);
  };

  const handleBulkDownload = async () => {
    try {
      setIsBulkDownloading(true);
      if (checkedRows?.length <= 0) {
        swal.fire({
          text: trans.fetchLabelKeyTranslation("NoCallSelectedText", "No calls selected to download. Please select one or more calls."),
          confirmButtonText: "Ok",
        })
      }
      else if(checkedRows?.length > bulkDownloadRowLimit) {
        swal.fire({
          text: `${trans.fetchLabelKeyTranslation("BulkRowCountWarning", "Selected row count cannot be greater than")}: ${bulkDownloadRowLimit}`,
          confirmButtonText: "Ok",
        })
        return;
      }
       else {
        let totalBytes = 0;
        checkedRows.forEach((recordingId) => {
          const matchingCall = calls?.calls.find(call =>
            call.recordings?.[0]?.id === recordingId
          );

          if (matchingCall) {
            totalBytes += matchingCall.recordings[0].recordingSizeInBytes || 0;
          } else {
            // console.error(`Recording ID ${recordingId} not found in calls`);
          }
        });

        const totalMBs = totalBytes / (1024 * 1024);
        if (totalMBs > bulkDownloadSizeLimitMB) {
          const bulkDownloadSizeInGb = bulkDownloadSizeLimitMB / 1024;
          swal.fire({
            text: `${trans.fetchLabelKeyTranslation("TotalDownloadSizeText", "The total download size cannot be greater than")}: ${bulkDownloadSizeInGb} GB.`,
            confirmButtonText: "Ok",
          });
          return;
        }

        const response = await recordingsService.bulkdownloadV2Request(checkedRows);
        if (response) {
          swal.fire({
            title: `${trans.fetchLabelKeyTranslation("TotalCallsSelectedText", "Total Calls Selected")}: ${checkedRows?.length}`,
            html: `
                  <div style="font-size: 14px; margin-top: 5px;">
                    <div><strong>${trans.fetchLabelKeyTranslation("TotalSizeText", "Total Size")}:</strong> ${totalMBs.toFixed(2)} MB </div>
                    <div style="margin-top: 10px; color: #555;">
                     ${trans.fetchLabelKeyTranslation("ReceiveMailText", "Note: You will receive an email with the download link once the bulk download is ready.")} 
                    </div>
                  </div>
                `,
            confirmButtonText: "Ok",
          });
          handleClearAllRows();
        }
      }
    }
    catch (err) {
      if (err instanceof AxiosError) {
        let errorMessage = err?.response?.data?.error;
        let translatedErrorMessage = trans.fetchLabelKeyTranslation("BulkFailedText", "Bulk Download Failed");

        if (errorMessage.includes("Recordings not found")) {
          translatedErrorMessage = trans.fetchLabelKeyTranslation("RecordingsNotFoundText", "Recordings not found");
        }
        if (errorMessage.includes("User does not have permission")) {
          translatedErrorMessage = trans.fetchLabelKeyTranslation("NoPermisisionText", "User does not have permission");
        }
        else if (errorMessage.includes("The total download size exceeds")) {
          translatedErrorMessage = trans.fetchLabelKeyTranslation("BulkSizeExceededText", "The total download size exceeds");
        }
        else if (errorMessage.includes("Unable to download any recording")) {
          translatedErrorMessage = trans.fetchLabelKeyTranslation("UnableToDownloadText", "Unable to download any recording");
        }

        const swalOptions: SweetAlertOptions = {
          icon: "error",
          title: trans.fetchLabelKeyTranslation("SwtAltErrorTitle", "Error"),
          text: translatedErrorMessage,
        };
        swal.fire(swalOptions);
      }
    }
    finally {
      setIsBulkDownloading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowExportDropdown(false);
      }
    };

    if (showExportDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportDropdown]);

  const handleClearAllRows = () => {
    setCheckedRows([]);
    setFilteredCalls(undefined);
    setIsShowCheckedRows(false);
  }
  
  const toggleCheckedRowsView = () => {
    // If currently showing checked rows, just go back to original grid keeping checkedRows intact
    if (isShowCheckedRows) {
      setIsShowCheckedRows(false);
      return;
    }

     // Build filteredCalls by combining previously accumulated selected calls (filteredCalls?.calls)
    // with any matches from the current page so selections from other pages are preserved.
    if (checkedRows?.length > 0) {
      const existingSelected = filteredCalls?.calls ?? [];

      const pageMatches =
        (calls?.calls ?? []).filter((call) =>
          call.recordings.some((r) => checkedRows.includes(r.id))
        );

      // Merge and dedupe by call.id (preserve any earlier selections stored in existingSelected)
      const map = new Map<number, typeof pageMatches[0]>();
      existingSelected.forEach((c) => map.set(c.id, c));
      pageMatches.forEach((c) => map.set(c.id, c));
      let matchingCalls = Array.from(map.values());

       // Determine current sort (use server page sort if available, otherwise default to DateTime desc)
      const currentSort = calls?.page?.sort ?? initialPageDetail.sort;
      let sortFieldProp = "callTime";
      switch (currentSort?.sortField) {
        case CallTableSortColumnEnum.DateTime:
          sortFieldProp = "callTime";
          break;
        case CallTableSortColumnEnum.FirstParty:
          sortFieldProp = "firstParty.email";
          break;
        case CallTableSortColumnEnum.SecondParty:
          sortFieldProp = "secondParty.email";
          break;
        case CallTableSortColumnEnum.Duration:
          sortFieldProp = "callDuration";
          break;
        case CallTableSortColumnEnum.Comment:
          sortFieldProp = "recordings[0].totalComments";
          break;
        case CallTableSortColumnEnum.Outcome:
          sortFieldProp = "callResult.name";
          break;
        default:
          sortFieldProp = "callTime";
      }

      const sortDir = (currentSort?.sortDirection ?? "desc") === "asc" ? "asc" : "desc";
      // Apply sorting to the merged selected calls
      matchingCalls = orderBy(matchingCalls, [{ field: sortFieldProp, dir: sortDir }]);

      setFilteredCalls({
        id: "tempIdForShowingCheckedRows",
        page: {
          skip: 0,
          take: calls?.page?.take ?? initialPageDetail.take,
          totalRecords: matchingCalls.length,
          sort:
            calls?.page?.sort ?? {
              sortField: CallTableSortColumnEnum.DateTime,
              sortDirection: "desc",
            },
        },
        calls: matchingCalls,
      });

      setIsShowCheckedRows(true);
    }
   }

  return (
    <React.Fragment>
      <div className="row p-t-10 p-r-20 p-l-20 p-b-1">
        <div className="col-md-12">

          {isBulkDownloading && (
            <LoadingOverlay
              customStyle={{ position: "fixed", marginTop: "55px" }}
              themeColor={"light"}
              size={"medium"}
              loadingText={trans.fetchLabelKeyTranslation("BulkDownloadingText", "BulkDownloading")}
            />
          )}

          <div className="bg-white d-flex justify-content-between flex-wrap mb-2">
            <span className="font-weight-semi d-flex align-items-center">
              {viewByMetricFilterValue
                ? `${trans.fetchLabelKeyTranslation("TextCallsFor", "Calls for")
                } ${trans.fetchLabelKeyTranslation(viewByMetricFilterValue.metricKeyLabel, viewByMetricFilterValue.metricKeyLabel)} = "${viewByMetricFilterValue.displayName
                  ? viewByMetricFilterValue.displayName
                  : viewByMetricFilterValue.metricKey
                }${viewByMetricFilterValue.metricKeyLabel === "Topics" &&
                  viewByMetricFilterValue.metricSubKeyLabel !==
                  "ExactMatchTopic"
                  ? " & " + viewByMetricFilterValue.metricSubKeyLabel
                  : ""
                }"`
                : `${trans.fetchLabelKeyTranslation("TextCalls", "Calls")
                }`}


              {checkedRows?.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginLeft: '50px',
                  }}
                >
                  <span style={{ fontWeight: 500 }}>Checked Rows:</span>

                  <span
                    onClick={toggleCheckedRowsView}
                    title= {isShowCheckedRows ? "Show All Calls" : "Show Checked Rows"}
                    style={{
                      cursor: 'pointer',
                      color: isShowCheckedRows ? '#0056b3' : '#007bff',
                      textDecoration: isShowCheckedRows ? 'underline' : 'none',
                      fontWeight: isShowCheckedRows ? 600 : 500,
                      borderBottom: isShowCheckedRows ? '2px solid #0056b3' : 'none',
                      transition: 'color 0.2s ease',
                    }}
                    onMouseEnter={e => { if (!isShowCheckedRows) e.currentTarget.style.color = '#0056b3';}}
                    onMouseLeave={e => { if (!isShowCheckedRows) e.currentTarget.style.color = '#007bff'; }}
                  >
                    {checkedRows.length}
                  </span>

                  <span
                    onClick={handleClearAllRows}
                    title="Clear checked rows"
                    style={{
                      cursor: 'pointer',
                      color: '#dc3545',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '16px',
                      transition: 'color 0.2s ease',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#a71d2a')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#dc3545')}
                  >
                    X
                  </span>
                </div>
              )}

            </span>
            <div className="buttons-container hov-transparent d-flex align-itmes-center">
              <div className="searchBox searchCol">
                <CustomSearchFieldTextInput
                  className="input-search"
                  placeholder={trans.fetchLabelKeyTranslation("SearchPlaceholder", "Search...")}
                  value={search}
                  updateValue={setSearch}
                  searchTextChangeHandler={handleSearchChange}
                  onEscapeKeyFunc={true}
                  handleOnKeyDown={handleSearchTextOnKeyDown}
                  disabled={isShowCheckedRows}
                />
              </div>

              <div
                className="position-relative"
                ref={dropdownRef}
                style={{ display: "inline-block" }}
              >
                <Button
                  className="btn bg-primary text-white m-l-8"
                  onClick={toggleExportDropdown}
                  style={{ height: "28px" }}
                  title={trans.fetchLabelKeyTranslation("ExportOrDownloadText", "Export/Download")}
                >
                  <i className="bi bi-cloud-download-fill"></i>
                </Button>

                {showExportDropdown && (
                  <div className="export-dropdown">
                    <div className="dropdown-item d-flex align-items-start p-2 cursor-pointer border-bottom"
                      onClick={handleExcelExport} >
                      <i className="bi bi-filetype-xls mr-2 fs-22 text-success"></i>
                      <div style={{ lineHeight: "1.5", }}>
                        <p className="mb-0">{trans.fetchLabelKeyTranslation("ExportText", "Export")}
                          <small className="text-muted d-block">{trans.fetchLabelKeyTranslation("ExportInFormatText", "Export in .xlsx format")}</small>
                        </p>
                      </div>
                    </div>
                    {auth?.checkUserAccess(AccessPermissionEnum.DownloadCalls) && (
                    <div className="dropdown-item d-flex align-items-start p-2 cursor-pointer"
                      onClick={handleBulkDownload} >
                      <i className="bi bi-download mr-2 fs-22 text-success"></i>
                      <div style={{ lineHeight: "1.5", }}>
                        <p className="mb-0">{trans.fetchLabelKeyTranslation("CallDownloadText", "Call Download")}
                          <small className="text-muted d-block">
                            {trans.fetchLabelKeyTranslation("BulkDownloadText", "Bulk download of {0} call records.").replace("{0}", String(bulkDownloadRowLimit))}
                          </small>
                        </p>
                      </div>
                    </div>)
                    }
                  </div>
                )}
              </div>

              {
                isExportDialogOpen && (
                  <ExportCallsDialog
                    checkedRows={checkedRows}
                    setCheckedRows={setCheckedRows}
                    onClose={onCloseExportDialog}
                    callData={calls?.calls}
                    totalRecords={calls?.page?.totalRecords}
                    filter={insightsFilter}
                    insightId={insight?.id}
                    filterKey={filterMetric ? filterMetric.metricKeyLabel : undefined}
                    filterSubKey={filterMetric ? filterMetric.metricKeySubLabel : undefined}
                    filterValue={filterMetric ? filterMetric.metricKey : undefined}
                    viewBy={viewByMetricFilterValue ? viewByMetricFilterValue.metricKeyLabel : undefined}
                    viewByFilterSubKey={viewByMetricFilterValue ? viewByMetricFilterValue.metricSubKeyLabel : undefined}
                    viewByFilterValue={viewByMetricFilterValue ? viewByMetricFilterValue.metricKey : undefined}
                  />
                )
              }

              {viewByMetricFilterValue && (
                <Button
                  className="line-height-1 text-white p-r-1"
                  style={{ height: "30px" }}
                >
                  <Link
                    to={"#"}
                    onClick={onUserBackClick}
                    className="btn-link text-primary p-0 m-0 d-flex align-items-center"
                  >
                    <i className="bi bi-arrow-left fs-21 p-r-5 line-height-1"></i>
                    <span className="fs-13 font-weight-semi">
                      {trans.fetchLabelKeyTranslation("BackBtnText", "Back to Grid")}
                    </span>
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <CallListing
        isBasic={!docked}
        loading={loading}
        error={error}
        callData={(isShowCheckedRows && filteredCalls?.calls && filteredCalls?.calls?.length > 0) ? filteredCalls?.calls : calls?.calls}
        handleCallFlagUpdate={handleCallFlagUpdate}
        // When showing checked rows use client-side pagination (false),
        // otherwise use server-side pagination (true)
        enableServerSidePagination={!isShowCheckedRows}
        searchTerm={""}
        pageDetail={(isShowCheckedRows && filteredCalls?.calls && filteredCalls?.calls?.length > 0 && filteredCalls?.page) ? filteredCalls.page : calls?.page}
        handlePaginationChange={handlePaginationChange}
        handleSortingChange={handleSortChange}
        isCheckboxEnabled={true}
        checkedRows={checkedRows}
        setCheckedRows={setCheckedRows}
        setFilteredCalls={setFilteredCalls}
        handleClearAllRows={handleClearAllRows}
      />
    </React.Fragment>
  );
};

export default InsightCallsGrid;
